import { safeCall } from '@xemplo/safe-call';

type ActiveLocation = {
  pathname: string;
  search?: string;
};

/**
 *
 * If the exact-pathname match fails, `customApplicationUrl` (when supplied) —
 * the currently mounted app's own base url, e.g. "https://hire-dev.xemplo.team" —
 * is used as an exact-base-path fallback. This lets one nav item stay active
 * across every page the app can render at, not just its own configured path:
 * Hire's nav item points at "https://hire-dev.xemplo.team", but its "/" route
 * immediately redirects to "/dashboard" or "/login" before the nav ever
 * mounts, so a plain pathname match against those paths would never fire.
 * With `customApplicationUrl` set to "https://hire-dev.xemplo.team", both
 * "/dashboard" and "/login" fall back to matching on origin + base path
 * alone. The match must be exact, not just same-origin, since sibling apps
 * (Home, Contracts, Workers, Analytics) can share one origin at different
 * sub-paths.
 *
 * Apps like Hire or Payroll — whose own root redirects elsewhere, or that
 * add nested sub-routes (e.g. Workers' "/worker-list/:id") — only stay
 * highlighted across those pages when they pass their own base url as
 * `customApplicationUrl`. It's optional and additive: omitting it (the
 * default for every current caller) leaves exact-pathname matching
 * unchanged, so existing implementations are unaffected.
 *
 * @param url - the nav item's configured url (absolute, or relative/in-app).
 * @param location - react-router's `useLocation()` result for relative urls.
 * @param customApplicationUrl - the currently mounted app's own canonical base
 *   url; omit for nav items that always render at a stable, matchable path.
 */
export const isNavLinkActive = (
  url: string,
  location: ActiveLocation,
  customApplicationUrl?: string,
) => {
  if (url.startsWith('http')) {
    return isCrossAppLinkActive(url, customApplicationUrl);
  }

  return isRelativeLinkActive(url, location);
};

const isCrossAppLinkActive = (url: string, customApplicationUrl?: string) => {
  const [parsed] = safeCall(() => new URL(url));
  if (!parsed) return false;
  if (parsed.pathname === window.location.pathname) return true;
  if (!customApplicationUrl) return false;

  const [currentApp] = safeCall(() => new URL(customApplicationUrl));
  if (!currentApp) return false;
  return (
    currentApp.hostname === parsed.hostname && currentApp.pathname === parsed.pathname
  );
};

/**
 * A relative tab/nav url is active when its query string (if any) exactly
 * matches the current search, falling back to a path-only match when there's
 * no current search at all (covers a tab group whose default state has no
 * query param of its own). A no-query link (e.g. a tab group's "All" item)
 * is only active when no sibling filter tab's query is currently selected.
 */
const isRelativeLinkActive = (url: string, location: ActiveLocation) => {
  const queryIndex = url.indexOf('?');
  const path = queryIndex === -1 ? url : url.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : url.slice(queryIndex);

  if (query)
    return location.search ? location.search === query : path === location.pathname;
  if (location.search) return false;

  // A rendered href includes the basename, so the current (basename-relative)
  // pathname is a suffix of it; the leading "/" keeps the match on a boundary.
  return path === location.pathname || path.endsWith(location.pathname);
};
